import ChevronFilters from '@assets/icons/chevron-down.svg';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_PADDING } from '@/components/base-action-sheet';
import { FormattedMarkdown } from '@/components/formatted-markdown';
import {
  LoyaltyEnrollmentFormConfig,
  LoyaltyQuestionType,
  LoyaltySelectOption,
  LoyaltySelectQuestion,
} from '@/components/loyalty/loyalty-enrollment-form-types';
import { SCREEN_PADDING } from '@/styles';
import { ScrollView, Text, TextStyle, View } from 'react-native';
import { SelectInput } from '@/components/select-input';
import { TextInput } from '@/components/text-input';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// Returns options for a given select question based on the answers
// provided so far
export const getVisibleOptions = (
  question: LoyaltySelectQuestion,
  answers: Record<string, string>,
): LoyaltySelectOption[] => {
  if (!question.parent_key) {
    return question.options;
  }

  const parentAnswer = answers[question.parent_key];
  return parentAnswer
    ? question.options.filter((option) => option.parent_value === parentAnswer)
    : [];
};

interface Props {
  answers: Record<string, string>;
  config: LoyaltyEnrollmentFormConfig;
  isSubmitting?: boolean;
  onAnswerChange: (key: string, value: string) => void;
  onOpenOptions: (question: LoyaltySelectQuestion) => void;
  onSubmit: () => void;
}

export const LoyaltyEnrollmentFormContent: React.FC<Props> = ({
  answers,
  config,
  isSubmitting = false,
  onAnswerChange,
  onOpenOptions,
  onSubmit,
}) => {
  const [styles, theme] = useStyles();
  const { bottom } = useSafeAreaInsets();
  const { isSmallLayout } = useLayoutContext();
  const { description, questions, title } = config;

  const answersComplete = useMemo(() => {
    return questions.every(
      (question) =>
        question.required === false || Boolean(answers[question.key]?.trim()),
    );
  }, [answers, questions]);

  return (
    <ScrollView
      contentContainerStyle={[
        styles.scrollContent,
        { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
      ]}
      keyboardShouldPersistTaps="handled"
      style={styles.scroll}
    >
      <Text style={styles.title}>
        {title || i18n.t('loyalty.tell_us_about_yourself')}
      </Text>
      {description ? (
        <FormattedMarkdown
          markdown={description}
          textStyle={
            isSmallLayout ? styles.descriptionSmall : styles.description
          }
        />
      ) : null}
      <View style={styles.questions}>
        {questions.map((question) => {
          const value = answers[question.key] ?? '';

          switch (question.type) {
            case LoyaltyQuestionType.Select: {
              const options = getVisibleOptions(question, answers);
              const disabled = options.length === 0;
              const selected = options.find((option) => option.value === value);

              return (
                <SelectInput
                  disabled={disabled}
                  inputContainerStyle={[
                    styles.input,
                    disabled ? styles.inputDisabled : null,
                  ]}
                  key={question.key}
                  label={question.label}
                  onPress={() => onOpenOptions(question)}
                  placeholderTextColor={
                    disabled ? theme.sk.color.text.disabled : undefined
                  }
                  rightElement={
                    <ChevronFilters
                      fill={
                        disabled
                          ? theme.colors.inactiveFill
                          : theme.colors.primary
                      }
                      height={8}
                      width={14}
                    />
                  }
                  testID={`loyalty-enrollment-form.${question.key}`}
                  value={selected ? (selected.label ?? selected.value) : ''}
                />
              );
            }
            case LoyaltyQuestionType.Text:
              return (
                <TextInput
                  containerStyle={styles.input}
                  key={question.key}
                  label={value ? question.label : undefined}
                  maxLength={question.max_length}
                  onChangeText={(next) => onAnswerChange(question.key, next)}
                  placeholder={question.placeholder ?? question.label}
                  testID={`loyalty-enrollment-form.${question.key}`}
                  value={value}
                />
              );
            default:
              return null;
          }
        })}
      </View>
      <Button
        disabled={!answersComplete}
        loading={isSubmitting}
        onPress={onSubmit}
        testID="loyalty-enrollment-form.submit"
        trackingData={{ name: 'loyalty_enrollment_form.submit' }}
        type="primary"
      >
        {i18n.t('continue')}
      </Button>
    </ScrollView>
  );
};

const useStyles = createStyles((theme) => ({
  description: {
    ...(theme.sk.typography.paragraph.md as TextStyle),
    color: theme.sk.color.text.secondary,
  },
  descriptionSmall: {
    ...(theme.sk.typography.paragraph.sm as TextStyle),
    color: theme.sk.color.text.secondary,
  },
  input: {
    borderColor: theme.colors.stroke,
  },
  inputDisabled: {
    backgroundColor: theme.sk.color.bg.primary.disabled,
  },
  questions: {
    gap: theme.sk.spacing.spaceMedium,
    marginVertical: theme.sk.spacing.spaceSmall,
  },
  scroll: {
    flexShrink: 1,
  },
  scrollContent: {
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: DEFAULT_PADDING,
  },
  title: {
    ...(theme.sk.typography.heading.sm as TextStyle),
    color: theme.sk.color.text.primary,
  },
}));
